<template>
  <section>
    <b-overlay :show="loading">
      <validation-observer
        ref="refFormObserver"
        #default="{ handleSubmit, invalid }"
      >
        <b-form @submit.prevent="handleSubmit(onSubmit)">
          <b-row>
            <!-- Left col: form + table -->
            <b-col cols="12" lg="10" xl="10" md="9">
              <b-card no-body :aria-hidden="loading ? 'true' : null">

                <!-- Header -->
                <b-card-body class="pb-0">
                  <div class="d-flex justify-content-between flex-md-row flex-column mt-0">

                    <!-- Left: title + proveedor -->
                    <div>
                      <h3 class="text-primary">
                        {{ isNew ? 'Nueva recepción' : `Recepción #${materialRecepcionData.num_pedido_proveedor || materialRecepcionData.id}` }}
                      </h3>

                      <validation-provider
                        #default="validationContext"
                        name="Proveedor"
                        rules="required"
                      >
                        <b-form-group
                          label="Proveedor*"
                          label-for="proveedor"
                          :state="getValidationState(validationContext)"
                        >
                          <v-select
                            v-model="materialRecepcionData.fk_proveedor"
                            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                            :options="proveedores"
                            :reduce="(val) => val.id"
                            :clearable="false"
                            placeholder="Seleccione"
                            label="nombre_comercial"
                            :disabled="!isNew"
                          />
                          <b-form-invalid-feedback :state="getValidationState(validationContext)">
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>

                      <validation-provider #default="validationContext" name="Nº Order">
                        <b-form-group label="Nº Order" :state="getValidationState(validationContext)">
                          <b-form-input
                            v-model="materialRecepcionData.num_pedido_proveedor"
                            placeholder="PPXX-YY-MM"
                            :disabled="!isNew"
                          />
                        </b-form-group>
                      </validation-provider>
                    </div>

                    <!-- Right: badge + fecha -->
                    <div class="mt-md-0 mt-2">
                      <b-row align-h="end" class="pr-1 mb-2">
                        <b-badge :variant="estadoVariant" class="text-capitalize">
                          {{ materialRecepcionData.estado || 'nuevo' }}
                        </b-badge>
                      </b-row>
                      <div class="d-flex align-items-center mb-1">
                        <span class="title pr-1 pb-1">Fecha*: </span>
                        <validation-provider #default="validationContext" name="Fecha">
                          <b-form-group :state="getValidationState(validationContext)">
                            <flat-pickr
                              v-if="canEdit"
                              v-model="materialRecepcionData.fecha_recepcion"
                              placeholder="dd-mm-yyyy"
                              class="form-control"
                              :config="{
                                format: 'd-m-Y',
                                altFormat: 'd-m-Y',
                                allowInput: true,
                                altInput: true,
                                locale: Spanish
                              }"
                            />
                            <span v-else>{{ formatDate(materialRecepcionData.fecha_recepcion) }}</span>
                          </b-form-group>
                        </validation-provider>
                      </div>
                    </div>
                  </div>
                </b-card-body>

                <hr>

                <h3 class="pl-2 mb-1">Materiales</h3>

                <!-- Table: existing reception (edit mode) -->
                <b-table
                  v-if="!isNew"
                  :items="recepcionItems"
                  responsive
                  striped
                  :fields="tableHeadersExisting"
                >
                  <template #cell(refProd)="data">
                    <span>{{ data.item.refProd }}</span>
                  </template>
                  <template #cell(descripcion)="data">
                    <div class="d-flex align-items-center">
                      <span>{{ data.item.descripcion }}</span>
                      <router-link
                        v-if="data.item.id"
                        target="_blank"
                        style="padding: 0 0 0 6px"
                        :to="{ name: 'material-edit', params: { id: data.item.id } }"
                      >
                        <feather-icon icon="ExternalLinkIcon" size="16" />
                      </router-link>
                    </div>
                  </template>
                  <template #cell(stock_real)="data">
                    <span>{{ data.item.stock_real }}</span>
                  </template>
                  <template #cell(coste)="data">
                    <b-form-input
                      v-model="data.item.coste"
                      type="number"
                      step="0.01"
                      :disabled="!canEdit"
                      style="min-width: 90px"
                    />
                  </template>
                  <template #cell(unidades_esperadas)="data">
                    <span>{{ data.item.unidades_esperadas }}</span>
                  </template>
                  <template #cell(unidades_recibidas)="data">
                    <b-form-input
                      v-model.number="data.item.unidades_recibidas"
                      type="number"
                      min="0"
                      :disabled="!canEdit"
                      style="min-width: 90px"
                    />
                  </template>
                  <template #cell(unidades_procesadas)="data">
                    <span>{{ data.item.unidades_procesadas }}</span>
                  </template>
                  <template #cell(actions)="data">
                    <!-- Eliminables: líneas extra (sin persistir) o "fantasma" (esperadas=0 y procesadas=0) -->
                    <b-button
                      v-if="canEdit && canRemoveLine(data.item)"
                      size="sm"
                      variant="danger"
                      class="btn-icon"
                      @click="removeItem(data.index)"
                    >
                      <feather-icon icon="XIcon" />
                    </b-button>
                  </template>
                </b-table>

                <!-- Table: new reception (create mode) -->
                <b-table
                  v-else
                  :items="recepcionItems"
                  responsive
                  striped
                  :fields="tableHeadersNew"
                >
                  <template #cell(refProd)="data">
                    <validation-provider #default="validationContext" name="ref-prod" rules="required">
                      <b-form-group :state="getValidationState(validationContext)">
                        <v-select
                          v-model="data.item.refProd"
                          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                          :options="materiales"
                          label="codigo"
                          :clearable="false"
                          placeholder="Select Item"
                          @input="(val) => updateItemForm(data.index, val)"
                          append-to-body
                          style="min-width: 200px"
                        />
                        <b-form-invalid-feedback :state="getValidationState(validationContext)">
                          {{ validationContext.errors[0] }}
                        </b-form-invalid-feedback>
                      </b-form-group>
                    </validation-provider>
                  </template>
                  <template #cell(descripcion)="data">
                    <validation-provider #default="validationContext" name="Descripcion-Material" rules="required">
                      <b-form-group :state="getValidationState(validationContext)">
                        <div class="d-flex align-items-center">
                          <v-select
                            v-model="data.item.descripcion"
                            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                            :options="materiales"
                            label="descripcion"
                            :clearable="false"
                            placeholder="Select Item"
                            @input="(val) => updateItemForm(data.index, val)"
                            append-to-body
                            style="min-width: 200px"
                          />
                          <router-link
                            v-if="data.item.id"
                            target="_blank"
                            style="padding: 0 0 0 6px"
                            :to="{ name: 'material-edit', params: { id: data.item.id } }"
                          >
                            <feather-icon icon="ExternalLinkIcon" size="16" />
                          </router-link>
                        </div>
                        <b-form-invalid-feedback :state="getValidationState(validationContext)">
                          {{ validationContext.errors[0] }}
                        </b-form-invalid-feedback>
                      </b-form-group>
                    </validation-provider>
                  </template>
                  <template #cell(stock_real)="data">
                    <span>{{ data.item.stock_real }}</span>
                  </template>
                  <template #cell(coste)="data">
                    <validation-provider #default="validationContext" name="Coste" rules="required">
                      <b-form-group :state="getValidationState(validationContext)">
                        <b-form-input v-model="data.item.coste" type="number" step="0.01" />
                        <b-form-invalid-feedback :state="getValidationState(validationContext)">
                          {{ validationContext.errors[0] }}
                        </b-form-invalid-feedback>
                      </b-form-group>
                    </validation-provider>
                  </template>
                  <template #cell(cantidad)="data">
                    <validation-provider #default="validationContext" name="Cantidad" rules="required|min_value:1">
                      <b-form-group :state="getValidationState(validationContext)">
                        <b-form-input v-model="data.item.cantidad" type="number" />
                        <b-form-invalid-feedback :state="getValidationState(validationContext)">
                          {{ validationContext.errors[0] }}
                        </b-form-invalid-feedback>
                      </b-form-group>
                    </validation-provider>
                  </template>
                  <template #cell(actions)="data">
                    <b-button
                      size="sm"
                      variant="danger"
                      class="btn-icon"
                      @click="removeItem(data.index)"
                    >
                      <feather-icon icon="XIcon" />
                    </b-button>
                  </template>
                </b-table>

                <b-row class="px-1">
                  <b-card-body class="form-item-section">
                    <b-button
                      v-if="canEdit"
                      v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                      size="sm"
                      variant="primary"
                      @click="selectMaterialModal = true"
                    >
                      Añadir Material
                    </b-button>
                  </b-card-body>
                </b-row>

                <hr>
              </b-card>
            </b-col>

            <!-- Right col: action buttons -->
            <b-col cols="12" lg="2" md="3" xl="2" class="pedido-actions mt-md-0 mt-2">
              <b-card>
                <b-button
                  v-if="canEdit"
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="primary"
                  class="mb-75"
                  block
                  type="submit"
                  :disabled="invalid"
                >
                  Guardar stocks
                </b-button>

                <b-button
                  v-if="materialRecepcionData.estado === 'completada'"
                  variant="success"
                  block
                  disabled
                >
                  Recepción completada
                </b-button>

                <b-button
                  v-if="materialRecepcionData.estado === 'completada'"
                  v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                  variant="outline-warning"
                  class="mb-75"
                  block
                  @click="reabrirRecepcion"
                >
                  <feather-icon icon="RotateCcwIcon" class="mr-50" />
                  Reabrir recepción
                </b-button>

                <b-button
                  v-if="!isNew && materialRecepcionData.fk_pedido_proveedor"
                  v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                  variant="outline-warning"
                  class="mb-75"
                  block
                  :to="{ name: 'pedido-proveedor-edit', params: { id: materialRecepcionData.fk_pedido_proveedor } }"
                >
                  <feather-icon icon="ExternalLinkIcon" class="mr-50" />
                  Ver pedido
                </b-button>

                <b-button
                  v-if="!isNew"
                  v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                  variant="outline-secondary"
                  class="mb-75"
                  block
                  @click="imprimirRecepcion"
                >
                  <feather-icon icon="PrinterIcon" class="mr-50" />
                  Imprimir albarán
                </b-button>

                <b-button
                  v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                  variant="outline-primary"
                  block
                  :to="{ name: 'materiales-recepcion-list' }"
                >
                  Lista recepciones
                </b-button>
              </b-card>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-overlay>

    <!-- Material selection modal -->
    <b-modal
      v-model="selectMaterialModal"
      :size="isMovil ? 'select-material-movil' : 'xl'"
      title="Seleccionar material"
      hide-footer
      hide-header-close
      no-close-on-backdrop
      no-close-on-esc
    >
      <b-row align-v="start" class="ml-0 pl-0">
        <b-col cols="12" lg="1" md="1" sm="2">
          <b-form-select
            id="perPageSelect"
            v-model="materialPerPage"
            :options="materialPerPageOptions"
          />
        </b-col>
        <b-col cols="12" lg="2" md="62" sm="12">
          <b-form-input
            v-model="searchMaterial"
            placeholder="Buscar..."
            class="form-control-merge"
            type="search"
          />
        </b-col>


        <!--         <b-col cols="12" lg="4" md="6" sm="12">
                  <b-row align-h="end" class="mx-0 px-0">

                  </>
                </b-col>
              </b-row>

              <b-row class="mx-0 px-0 pt-2">-->
        <b-col cols="12" lg="2" md="2" sm="6">
          <v-select
            v-model="proveedorFilter"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="proveedores"
            :reduce="(val) => val.id"
            :clearable="true"
            class="mb-1 item-selector-title"
            placeholder="Filtrar por proveedor"
            label="nombre_comercial"
          />
        </b-col>
        <b-col cols="12" lg="2" md="2" sm="6">
          <v-select
            v-model="tipoFilter"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="tipos"
            :clearable="true"
            class="mb-1 item-selector-title"
            placeholder="Tipo Material"
          />
        </b-col>
        <b-col cols="12" lg="1">
          <b-button
              v-if="isMovil"
              variant="primary"
              class="btn-icon"
              block
              @click="addSelectedMaterials"
          >
            <feather-icon icon="SaveIcon" />
          </b-button>
          <b-button v-else block variant="primary" @click="addSelectedMaterials">
            Guardar
          </b-button>
        </b-col>
        <b-col cols="12" lg="1">
          <b-button
              v-if="isMovil"
              block
              variant="warning"
              class="btn-icon"
              @click="selectMaterialModal = false"
          >
            <feather-icon icon="XIcon" />
          </b-button>
          <b-button v-else block variant="danger" @click="selectMaterialModal = false">
            Cancelar
          </b-button>
        </b-col>
        <b-col cols="12" lg="1" md="1" sm="12">
          <b-button block variant="outline-danger" @click="clearMaterialFilter">
            Limpiar
          </b-button>
        </b-col>
        <b-col cols="12" lg="2" md="2" sm="12">
          <b-button block variant="success" @click="openQuickCreate">
            <feather-icon icon="PlusIcon" class="mr-25" />
            Crear nuevo material
          </b-button>
        </b-col>
      </b-row>

      <b-table
        :items="materialesForTableFilter"
        responsive
        :fields="materialTableHeaders"
        :current-page="currentPageMaterial"
        :per-page="materialPerPage"
        class="position-relative rows-responsive"
        selectable
        select-mode="multi"
        select-variant="light"
        :tbody-tr-class="rowClass"
        :filter="searchMaterial"
        stacked="sm"
        @row-clicked="onRowSelected"
      >
        <template #cell(selected)="data">
          <feather-icon size="20" :icon="data.item.selected ? 'CheckCircleIcon' : 'CircleIcon'" />
        </template>
        <template #cell(codigo)="data">#{{ data.item.codigo }}</template>
        <template #cell(descripcion)="data">
          {{ data.item.descripcion_custom ? data.item.descripcion_custom : data.item.descripcion }}
        </template>
        <template #cell(coste)="data"><span>{{ data.item.coste }}€</span></template>
        <template #cell(estado)="data">
          <b-badge pill :variant="`light-${resolveStatusVariant(data.item.estado)}`" class="text-capitalize">
            {{ resolveStatusText(data.item.estado) }}
          </b-badge>
        </template>
        <template #cell(unidades)="data">
          <validation-provider #default="validationContext" name="Cantidad" rules="min_value:1">
            <b-form-group :state="getValidationState(validationContext)">
              <b-form-input v-model="data.item.unidades" placeholder="Unidades" type="number" />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>
        </template>
      </b-table>

      <b-pagination
        v-model="currentPageMaterial"
        :total-rows="materialesForTableFilter.length"
        :per-page="materialPerPage"
        first-number
        last-number
        class="mb-0 mt-1 mt-sm-0"
        prev-class="prev-item"
        next-class="next-item"
      />
    </b-modal>

    <!-- Quick material creation modal -->
    <b-modal
      v-model="quickCreateModal"
      title="Crear nuevo material"
      ok-title="Crear"
      cancel-title="Cancelar"
      ok-variant="success"
      @ok.prevent="submitQuickCreate"
    >
      <validation-observer ref="quickCreateObserver">
        <b-form>
          <b-form-group label="Descripción*" label-for="qc-descripcion">
            <validation-provider #default="v" name="Descripción" rules="required">
              <b-form-input
                id="qc-descripcion"
                v-model="quickCreateForm.descripcion"
                :state="getValidationState(v)"
                placeholder="Nombre del material"
              />
              <b-form-invalid-feedback>{{ v.errors[0] }}</b-form-invalid-feedback>
            </validation-provider>
          </b-form-group>

          <b-form-group label="Coste unitario (€)*" label-for="qc-coste">
            <validation-provider #default="v" name="Coste" rules="required">
              <b-form-input
                id="qc-coste"
                v-model="quickCreateForm.coste"
                type="number"
                step="0.01"
                min="0"
                :state="getValidationState(v)"
                placeholder="0.00"
              />
              <b-form-invalid-feedback>{{ v.errors[0] }}</b-form-invalid-feedback>
            </validation-provider>
          </b-form-group>

          <b-form-group label="Tipo*" label-for="qc-tipo">
            <validation-provider #default="v" name="Tipo" rules="required">
              <v-select
                id="qc-tipo"
                v-model="quickCreateForm.tipo"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="tipos"
                :reduce="opt => opt.value"
                label="label"
                placeholder="Seleccione tipo"
              />
              <small v-if="v.errors[0]" class="text-danger">{{ v.errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group label="Medida*" label-for="qc-medida">
            <validation-provider #default="v" name="Medida" rules="required">
              <v-select
                id="qc-medida"
                v-model="quickCreateForm.medida"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="medidas"
                :reduce="opt => opt.value"
                label="label"
                placeholder="Seleccione medida"
              />
              <small v-if="v.errors[0]" class="text-danger">{{ v.errors[0] }}</small>
            </validation-provider>
          </b-form-group>
        </b-form>
      </validation-observer>
    </b-modal>
  </section>
</template>

<script>
import {
  BCard,
  BCardBody,
  BRow,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BButton,
  BFormInvalidFeedback,
  BOverlay,
  BModal,
  BTable,
  BBadge,
  BPagination,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { ref, onUnmounted } from '@vue/composition-api'
import { Spanish } from 'flatpickr/dist/l10n/es'
import Ripple from 'vue-ripple-directive'
import { required } from '@validations'
import formValidation from '@core/comp-functions/forms/form-validation'
import { isMobile } from 'mobile-device-detect'
import { materiales as materialesUtils } from '@/utils/materiales'
import router from '@/router'
import store from '@/store'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import MaterialesRecepcion from '@/models/MaterialesRecepcion'
import MaterialesRecepcionDetalles from '@/models/MaterialesRecepcionDetalles'
import useMaterialesList from '@/views/almacen/materiales/useMaterialesList'
import materialesStoreModule from '@/views/almacen/materiales/materialesStoreModule'
import useProveedoresList from '@/views/system/proveedor/useProveedoresList'
import proveedorStoreModule from '@/views/system/proveedor/proveedorStoreModule'
import materialesRecepcionStoreModule from './materialesRecepcionStoreModule'

export default {
  components: {
    BCard,
    BCardBody,
    BRow,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BButton,
    BFormInvalidFeedback,
    BOverlay,
    BModal,
    BTable,
    BBadge,
    BPagination,
    vSelect,
    flatPickr,
    ValidationProvider,
    ValidationObserver,
  },

  directives: {
    Ripple,
  },

  data() {
    return {
      Spanish,
      required,
      selectMaterialModal: false,
      quickCreateModal: false,
      quickCreateForm: {},
      selected: [],
      searchMaterial: '',
      currentPageMaterial: 1,
      materialPerPage: 10,
      materialPerPageOptions: [10, 20, 50],
      isMovil: isMobile,
      proveedorFilter: null,
      tipoFilter: null,
      tipos: materialesUtils.tipos,
      medidas: materialesUtils.medidas,
      materialTableHeaders: [
        { key: 'selected', label: '', sortable: false },
        { key: 'codigo', label: 'Código', sortable: true },
        { key: 'descripcion', label: 'Descripción', sortable: true },
        { key: 'unidades', label: 'Cantidad', sortable: false },
        { key: 'coste', label: 'Coste', sortable: true },
        { key: 'stock', label: 'Stock', sortable: true },
        { key: 'tipo', label: 'Tipo', sortable: true },
        { key: 'estado', label: 'Estado', sortable: true },
      ],
    }
  },

  computed: {
    user() {
      return this.$store.state.auth.user
    },
    loading() {
      return this.$store.state['app-materiales-recepcion'].loading
    },
    isNew() {
      return this.$route.params.id === 'new'
    },
    canEdit() {
      if (this.isNew) return true
      return this.materialRecepcionData?.estado !== 'completada'
    },
    estadoVariant() {
      const map = { completada: 'success', parcial: 'warning', pendiente: 'secondary' }
      return map[this.materialRecepcionData?.estado] || 'secondary'
    },
    materialesForTableFilter() {
      if (!this.materiales) return []
      let items = this.materiales.filter(m =>
        m.descripcion?.toLowerCase().includes(this.searchMaterial.toLowerCase())
        || m.descripcion_custom?.toLowerCase().includes(this.searchMaterial.toLowerCase())
        || String(m.codigo).toLowerCase().includes(this.searchMaterial.toLowerCase()),
      )
      if (this.tipoFilter) items = items.filter(m => m.tipo === this.tipoFilter.value)
      if (this.proveedorFilter) items = items.filter(m => m.fk_proveedor === this.proveedorFilter)
      return items.map(m => ({ ...m, unidades: m.unidades !== undefined ? m.unidades : 1 }))
    },
  },

  methods: {
    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleDateString('es-ES')
    },
    imprimirRecepcion() {
      this.$store
        .dispatch('app-materiales-recepcion/printRecepcion', this.$route.params.id)
        .then(response => {
          if (response?.route) window.open(response.route, '_blank')
        })
        .catch(error => {
          console.error('Error al generar el PDF:', error)
        })
    },
    openQuickCreate() {
      this.quickCreateForm = {
        descripcion: '',
        coste: '',
        tipo: null,
        medida: null,
        fk_proveedor: this.materialRecepcionData?.fk_proveedor || null,
        estado: 'activo',
      }
      this.quickCreateModal = true
    },
    async submitQuickCreate() {
      const valid = await this.$refs.quickCreateObserver.validate()
      if (!valid) return
      const newMat = await this.$store.dispatch('app-materiales/createMaterialRapido', this.quickCreateForm)
      if (!newMat) return
      if (this.materiales) this.materiales.push(newMat)
      if (this.isNew) {
        this.recepcionItems.push({
          id: newMat.id,
          fk_material: newMat.id,
          refProd: newMat.codigo,
          descripcion: newMat.descripcion,
          stock_real: newMat.stock_real,
          coste: newMat.coste,
          cantidad: 1,
        })
      } else {
        this.recepcionItems.push({
          detalle_id: null,
          id: newMat.id,
          fk_material: newMat.id,
          refProd: newMat.codigo,
          descripcion: newMat.descripcion,
          stock_real: newMat.stock_real,
          coste: newMat.coste,
          unidades_esperadas: 0,
          unidades_recibidas: 1,
          unidades_procesadas: 0,
        })
      }
      this.quickCreateModal = false
      this.selectMaterialModal = false
    },
    rowClass(item, type) {
      if (!item || type !== 'row') return
      if (item.selected) return 'bg-light'
    },
    onRowSelected(item) {
      item.selected = !item.selected
      if (item.selected) {
        this.selected.push(item)
      } else {
        const idx = this.selected.findIndex(i => i.id === item.id)
        this.selected.splice(idx, 1)
      }
    },
    clearMaterialFilter() {
      this.searchMaterial = ''
      this.proveedorFilter = null
      this.tipoFilter = null
    },
    addSelectedMaterials() {
      this.selected.forEach(item => {
        if (this.isNew) {
          this.recepcionItems.push({
            id: item.id,
            fk_material: item.id,
            refProd: item.codigo,
            descripcion: item.descripcion_custom || item.descripcion,
            stock_real: item.stock_real,
            coste: item.coste,
            cantidad: Number(item.unidades) || 1,
          })
        } else {
          this.recepcionItems.push({
            detalle_id: null,
            id: item.id,
            fk_material: item.id,
            refProd: item.codigo,
            descripcion: item.descripcion_custom || item.descripcion,
            stock_real: item.stock_real,
            coste: item.coste,
            unidades_esperadas: 0,
            unidades_recibidas: Number(item.unidades) || 1,
            unidades_procesadas: 0,
          })
        }
      })
      this.materiales.forEach(m => { m.selected = false })
      this.selected = []
      this.searchMaterial = ''
      this.selectMaterialModal = false
    },
    // Una línea es eliminable si es extra (no persistida) o "fantasma": esperadas=0,
    // recibidas=0 y procesadas=0 (vacía de verdad — no perder cantidades ya introducidas).
    canRemoveLine(item) {
      if (!item.detalle_id) return true
      return Number(item.unidades_esperadas) === 0
        && Number(item.unidades_recibidas) === 0
        && Number(item.unidades_procesadas) === 0
    },
    async reabrirRecepcion() {
      const res = await this.$store.dispatch('app-materiales-recepcion/reabrirRecepcion', this.$route.params.id)
      if (res === false) return
      // create devuelve la respuesta axios → estado en res.data.estado
      if (res && res.data && res.data.estado) this.materialRecepcionData.estado = res.data.estado
    },
    async removeItem(index) {
      const item = this.recepcionItems[index]
      // Líneas persistidas (con detalle_id) se borran en backend (guard: solo 0/0).
      if (item && item.detalle_id) {
        const res = await this.$store.dispatch('app-materiales-recepcion/eliminarDetalle', item.detalle_id)
        if (res === false) return
        // Refrescar el badge con el estado recalculado por el backend (delete devuelve la respuesta axios).
        if (res && res.data && res.data.estado) this.materialRecepcionData.estado = res.data.estado
      }
      this.recepcionItems.splice(index, 1)
    },
    updateItemForm(index, val) {
      const { codigo, id, coste, descripcion } = val
      this.recepcionItems[index].refProd = codigo
      this.recepcionItems[index].id = id
      this.recepcionItems[index].fk_material = id
      this.recepcionItems[index].coste = coste
      this.recepcionItems[index].descripcion = descripcion
      this.recepcionItems[index].stock_real = val.stock_real
    },
    onSubmit() {
      if (this.isNew) {
        this.materialRecepcionData.fk_user = this.user.id
        this.materialRecepcionData.detalles = this.recepcionItems.map(el =>
          new MaterialesRecepcionDetalles({
            fk_material: el.id,
            coste: el.coste,
            cantidad: el.cantidad,
          }),
        )
        this.$store
          .dispatch('app-materiales-recepcion/addMaterialRecepcion', this.materialRecepcionData)
          .then(() => {
            this.$router.push({ name: 'materiales-recepcion-list' })
          })
      } else {
        const detalles = this.recepcionItems.map(el => {
          if (el.detalle_id) {
            return { id: el.detalle_id, unidades_recibidas: Number(el.unidades_recibidas), coste: el.coste }
          }
          return { fk_material: el.fk_material, unidades_recibidas: Number(el.unidades_recibidas), coste: el.coste }
        })
        this.$store
          .dispatch('app-materiales-recepcion/recibirMercancia', {
            id: this.$route.params.id,
            detalles,
          })
          .then(() => {
            this.$router.push({ name: 'materiales-recepcion-list' })
          })
      }
    },
  },

  setup() {
    const RECEPCION_MODULE  = 'app-materiales-recepcion'
    const PROVEEDOR_MODULE  = 'app-proveedor'
    const MATERIALES_MODULE = 'app-materiales'

    if (!store.hasModule(RECEPCION_MODULE))  store.registerModule(RECEPCION_MODULE, materialesRecepcionStoreModule)
    if (!store.hasModule(PROVEEDOR_MODULE))  store.registerModule(PROVEEDOR_MODULE, proveedorStoreModule)
    if (!store.hasModule(MATERIALES_MODULE)) store.registerModule(MATERIALES_MODULE, materialesStoreModule)

    onUnmounted(() => {
      if (store.hasModule(RECEPCION_MODULE))  store.unregisterModule(RECEPCION_MODULE)
      if (store.hasModule(PROVEEDOR_MODULE))  store.unregisterModule(PROVEEDOR_MODULE)
      if (store.hasModule(MATERIALES_MODULE)) store.unregisterModule(MATERIALES_MODULE)
    })

    const blankRecepcion = new MaterialesRecepcion({
      fecha_recepcion: new Date().toISOString().split('T')[0],
    })
    const materialRecepcionData = ref(null)
    const recepcionItems = ref([])
    const proveedores = ref([])
    const materiales = ref(null)

    const { listMateriales, resolveStatusText, resolveStatusVariant } = useMaterialesList()
    const { listProveedores } = useProveedoresList()

    listMateriales().then(res => { materiales.value = res })
    listProveedores().then(res => { proveedores.value = res })

    const tableHeadersNew = ref([
      { key: 'refProd', label: 'REF PROD', sortable: false },
      { key: 'descripcion', label: 'Descripción', sortable: false },
      { key: 'stock_real', label: 'Stock real' },
      { key: 'coste', label: 'Coste Unit. (€)' },
      { key: 'cantidad', label: 'Cantidad', sortable: false },
      { key: 'actions', label: '', thStyle: { minWidth: '80px' } },
    ])

    const tableHeadersExisting = ref([
      { key: 'refProd', label: 'REF PROD' },
      { key: 'descripcion', label: 'Descripción' },
      { key: 'stock_real', label: 'Stock real' },
      { key: 'coste', label: 'Coste (€)' },
      { key: 'unidades_esperadas', label: 'Esperadas' },
      { key: 'unidades_recibidas', label: 'Recibidas' },
      { key: 'unidades_procesadas', label: 'Procesadas' },
      { key: 'actions', label: '', thStyle: { minWidth: '60px' } },
    ])

    if (router.currentRoute.params.id !== 'new') {
      store
        .dispatch('app-materiales-recepcion/fetchMaterialRecepcion', router.currentRoute.params.id)
        .then(async response => {
          materialRecepcionData.value = response
          for (const detalle of (response.detalles || [])) {
            const material = await store.dispatch('app-materiales/fetchMaterial', detalle.fk_material)
            recepcionItems.value.push({
              detalle_id: detalle.id,
              id: material.id,
              fk_material: material.id,
              refProd: material.codigo,
              descripcion: material.descripcion,
              stock_real: material.stock_real,
              coste: detalle.coste,
              unidades_esperadas: detalle.unidades_esperadas,
              unidades_recibidas: detalle.unidades_recibidas,
              unidades_procesadas: detalle.unidades_procesadas,
            })
          }
        })
        .catch(err => {
          if (err.response?.status === 404) materialRecepcionData.value = undefined
        })
    } else {
      materialRecepcionData.value = blankRecepcion
    }

    const resetData = () => {
      materialRecepcionData.value = JSON.parse(JSON.stringify(blankRecepcion))
    }
    const { refFormObserver, getValidationState, resetForm } = formValidation(resetData)

    return {
      materialRecepcionData,
      recepcionItems,
      proveedores,
      materiales,
      tableHeadersNew,
      tableHeadersExisting,
      refFormObserver,
      getValidationState,
      resetForm,
      resolveStatusText,
      resolveStatusVariant,
    }
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>
<style>
.select-material-movil {
  width: 360px;
}
</style>
